import React, { useMemo, useState, useEffect } from 'react';
import type { Recipe, Language, Ingredient, PlannedMeal, DayOfWeek, MealPlan, MealType } from '../types';
import { getTranslation } from '../utils/translations';
import { resolveMealMenuTitle } from '../utils/mealSlotTitle';
import MenuOverviewScreen from './MenuOverviewScreen';
import PrepGatherScreen from './PrepGatherScreen';
import ActiveCookingSessionScreen from './ActiveCookingSessionScreen';
import PrepTimelineModal from './PrepTimelineModal';
import InfoNoticeModal from './InfoNoticeModal';
import {
    areAllMealRecipesStepsComplete,
    getRecipePrepIngredientKey,
    getRecipeStepKeys,
    groupRecipeInstructions,
    isRecipeStepsComplete,
} from '../utils/cookingSessionSteps';
import * as Firestore from 'firebase/firestore';
import { deleteField } from 'firebase/firestore';
import { db } from '../services/firebase';

interface MealCookingSessionProps {
    recipes: (PlannedMeal & { recipe: Recipe })[];
    allRecipes?: Recipe[];
    mealType: string;
    language?: Language;
    canManage?: boolean;
    isHidden?: boolean;
    onClose: () => void;
    onAddIngredientsToCart?: (ingredients: any[], recipeName: string) => Promise<void>;
    onViewRecipe?: (recipeId: string) => void;
    currentDay?: DayOfWeek;
    mealPlan?: MealPlan;
    orderedDays?: DayOfWeek[];
    activeHouseholdId?: string;
    weekOffset?: number;
    encryptedGeminiKey?: string;
    currentUserId?: string;
    onUpdateMealSlotTitle?: (day: DayOfWeek, mealType: MealType, title: string | null) => void;
    initialRecipeId?: string;
}

function getTimelineChartStorageKey(
    householdId: string | undefined,
    sessionKey: string,
    userId: string
): string {
    return `mealpai:timelineChart:${householdId ?? 'local'}:${sessionKey}:${userId}`;
}

function getTimelineOffsetStorageKey(
    householdId: string | undefined,
    sessionKey: string,
    userId: string
): string {
    return `mealpai:timelineOffsets:${householdId ?? 'local'}:${sessionKey}:${userId}`;
}

function normalizeTimelineOffsets(value: unknown): Record<string, number> {
    if (!value || typeof value !== 'object') return {};
    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
            .filter((entry): entry is [string, number] =>
                typeof entry[0] === 'string' && typeof entry[1] === 'number' && entry[1] > 0
            )
            .map(([id, minutes]) => [id, Math.round(minutes)])
    );
}

type CookingView = 'menu' | 'prep' | string;

const MealCookingSession: React.FC<MealCookingSessionProps> = ({
    recipes, allRecipes, mealType, language, canManage = false, isHidden = false, onClose, onAddIngredientsToCart, onViewRecipe,
    currentDay, mealPlan, orderedDays, activeHouseholdId, weekOffset, encryptedGeminiKey, currentUserId,
    onUpdateMealSlotTitle, initialRecipeId,
}) => {
    const t = (key: any) => getTranslation(language, key);

    const [view, setView] = useState<CookingView>(() => {
        if (initialRecipeId && recipes.some((meal) => meal.recipe.id === initialRecipeId)) {
            return initialRecipeId;
        }
        return 'menu';
    });
    const [isPrepTimelineOpen, setIsPrepTimelineOpen] = useState(false);
    const [struckIngredients, setStruckIngredients] = useState<Set<string>>(new Set());
    const [preppedIngredients, setPreppedIngredients] = useState<Set<string>>(new Set());
    const [servingsMap, setServingsMap] = useState<Record<string, number>>({});
    const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
    const [completedRecipes, setCompletedRecipes] = useState<Set<string>>(new Set());
    const [mealCompleted, setMealCompleted] = useState(false);
    const [cookingStartedAt, setCookingStartedAt] = useState<Date | null>(null);
    const [chartSelectedRecipeIds, setChartSelectedRecipeIds] = useState<string[] | undefined>(undefined);
    const [chartRecipeOffsets, setChartRecipeOffsets] = useState<Record<string, number>>({});
    const [isMealDonePromptOpen, setIsMealDonePromptOpen] = useState(false);
    const [mealDonePromptDismissed, setMealDonePromptDismissed] = useState(false);
    const [isMealCompletedNoticeOpen, setIsMealCompletedNoticeOpen] = useState(false);

    // Always resolve the freshest recipe data (so edits made via "Go to recipe" reflect on return)
    const liveRecipes = useMemo(() => recipes.map(mr => ({
        ...mr,
        recipe: allRecipes?.find(r => r.id === mr.recipe.id) ?? mr.recipe,
    })), [recipes, allRecipes]);

    const sessionKey = useMemo(() => {
        if (weekOffset === undefined || !currentDay || !mealType) return null;
        return `${weekOffset}_${currentDay}_${mealType}`;
    }, [weekOffset, currentDay, mealType]);

    const sessionDocRef = useMemo(() => {
        if (!activeHouseholdId || !sessionKey) return null;
        return Firestore.doc(db, 'households', activeHouseholdId, 'cookingSessions', sessionKey);
    }, [activeHouseholdId, sessionKey]);

    const sessionDateDisplay = useMemo(() => {
        if (!currentDay || !orderedDays || orderedDays.length === 0 || weekOffset === undefined) return '';

        const startDayOfWeek = orderedDays[0];
        const now = new Date();
        const currentDayIndex = now.getDay();
        const startDayIdx = startDayOfWeek === 'Sunday' ? 0 : 1;
        let diff = currentDayIndex - startDayIdx;
        if (startDayOfWeek === 'Monday' && currentDayIndex === 0) diff = 6;
        else if (diff < 0) diff += 7;

        const currentWeekStart = new Date(now);
        currentWeekStart.setDate(now.getDate() - diff);
        currentWeekStart.setHours(0, 0, 0, 0);

        const dayIndex = orderedDays.indexOf(currentDay);
        if (dayIndex === -1) return '';

        const targetDate = new Date(currentWeekStart);
        targetDate.setDate(currentWeekStart.getDate() + (weekOffset * 7) + dayIndex);

        const dd = String(targetDate.getDate()).padStart(2, '0');
        const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
        return `${dd}/${mm}`;
    }, [currentDay, orderedDays, weekOffset]);

    const sessionDateString = useMemo(() => {
        if (!currentDay || !orderedDays || orderedDays.length === 0 || weekOffset === undefined) return '';

        const startDayOfWeek = orderedDays[0];
        const now = new Date();
        const currentDayIndex = now.getDay();
        const startDayIdx = startDayOfWeek === 'Sunday' ? 0 : 1;
        let diff = currentDayIndex - startDayIdx;
        if (startDayOfWeek === 'Monday' && currentDayIndex === 0) diff = 6;
        else if (diff < 0) diff += 7;

        const currentWeekStart = new Date(now);
        currentWeekStart.setDate(now.getDate() - diff);
        currentWeekStart.setHours(0, 0, 0, 0);

        const dayIndex = orderedDays.indexOf(currentDay);
        if (dayIndex === -1) return '';

        const targetDate = new Date(currentWeekStart);
        targetDate.setDate(currentWeekStart.getDate() + (weekOffset * 7) + dayIndex);

        const yyyy = targetDate.getFullYear();
        const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
        const dd = String(targetDate.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }, [currentDay, orderedDays, weekOffset]);

    const recipesKey = useMemo(() => {
        return recipes.map(r => `${r.recipe.id}-${r.plannedServings}`).join(',');
    }, [recipes]);

    useEffect(() => {
        setChartSelectedRecipeIds(undefined);
        setChartRecipeOffsets({});
    }, [sessionKey, currentUserId]);

    useEffect(() => {
        if (sessionDocRef || !currentUserId || !sessionKey) return;

        try {
            const raw = localStorage.getItem(getTimelineChartStorageKey(activeHouseholdId, sessionKey, currentUserId));
            if (!raw) {
                setChartSelectedRecipeIds(undefined);
            } else {
                const parsed: unknown = JSON.parse(raw);
                setChartSelectedRecipeIds(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : undefined);
            }

            const rawOffsets = localStorage.getItem(getTimelineOffsetStorageKey(activeHouseholdId, sessionKey, currentUserId));
            if (!rawOffsets) {
                setChartRecipeOffsets({});
            } else {
                setChartRecipeOffsets(normalizeTimelineOffsets(JSON.parse(rawOffsets)));
            }
        } catch {
            setChartSelectedRecipeIds(undefined);
            setChartRecipeOffsets({});
        }
    }, [sessionDocRef, currentUserId, sessionKey, activeHouseholdId]);

    // Listen to Firestore for real-time cooking session state sync
    useEffect(() => {
        if (!sessionDocRef) return;

        const unsubscribe = Firestore.onSnapshot(sessionDocRef, async (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                const dbStruck = new Set<string>(data.struckIngredients || []);
                const dbPrepped = new Set<string>(data.preppedIngredients || []);
                const dbCompleted = new Set<string>(data.completedSteps || []);
                const dbCompletedRecipes = new Set<string>(data.completedRecipes || []);
                const dbServings = data.servingsMap || {};
                const dbMealCompleted = Boolean(data.mealCompleted);
                const dbCookingStartedAt = typeof data.cookingStartedAt === 'string'
                    ? new Date(data.cookingStartedAt)
                    : null;
                const dbChartSelection = currentUserId && data.timelineChartSelections?.[currentUserId];
                const nextChartSelection = Array.isArray(dbChartSelection)
                    ? dbChartSelection.filter((id): id is string => typeof id === 'string')
                    : undefined;
                const dbChartOffsets = currentUserId && data.timelineChartOffsets?.[currentUserId];
                const nextChartOffsets = normalizeTimelineOffsets(dbChartOffsets);

                const initialMap: Record<string, number> = {};
                recipes.forEach(mr => {
                    initialMap[mr.recipe.id] = mr.plannedServings;
                });
                const mergedServings = { ...initialMap, ...dbServings };

                const needsUpdate = Object.keys(initialMap).some(key => !(key in dbServings));
                if (needsUpdate) {
                    await Firestore.setDoc(sessionDocRef, {
                        servingsMap: mergedServings,
                        updatedAt: Firestore.serverTimestamp()
                    }, { merge: true });
                }

                setStruckIngredients(dbStruck);
                setPreppedIngredients(dbPrepped);
                setCompletedSteps(dbCompleted);
                setCompletedRecipes(dbCompletedRecipes);
                setMealCompleted(dbMealCompleted);
                setServingsMap(mergedServings);
                setCookingStartedAt(dbCookingStartedAt && !Number.isNaN(dbCookingStartedAt.getTime()) ? dbCookingStartedAt : null);
                setChartSelectedRecipeIds(nextChartSelection);
                setChartRecipeOffsets(nextChartOffsets);
            } else {
                const initialServings: Record<string, number> = {};
                recipes.forEach(mr => {
                    initialServings[mr.recipe.id] = mr.plannedServings;
                });

                await Firestore.setDoc(sessionDocRef, {
                    struckIngredients: [],
                    preppedIngredients: [],
                    completedSteps: [],
                    completedRecipes: [],
                    mealCompleted: false,
                    servingsMap: initialServings,
                    updatedAt: Firestore.serverTimestamp()
                });
            }
        }, (error) => {
            console.error("Firestore onSnapshot error:", error);
        });

        return () => unsubscribe();
    }, [sessionDocRef, recipesKey, recipes, currentUserId]);

    const handleChartRecipeSelectionChange = async (selectedIds: string[]) => {
        setChartSelectedRecipeIds(selectedIds);

        if (sessionDocRef && currentUserId) {
            await Firestore.updateDoc(sessionDocRef, {
                [`timelineChartSelections.${currentUserId}`]: selectedIds,
                updatedAt: Firestore.serverTimestamp(),
            });
            return;
        }

        if (currentUserId && sessionKey) {
            localStorage.setItem(
                getTimelineChartStorageKey(activeHouseholdId, sessionKey, currentUserId),
                JSON.stringify(selectedIds)
            );
        }
    };

    const handleChartRecipeOffsetsChange = async (offsets: Record<string, number>) => {
        setChartRecipeOffsets(offsets);

        if (sessionDocRef && currentUserId) {
            await Firestore.updateDoc(sessionDocRef, {
                [`timelineChartOffsets.${currentUserId}`]: offsets,
                updatedAt: Firestore.serverTimestamp(),
            });
            return;
        }

        if (currentUserId && sessionKey) {
            localStorage.setItem(
                getTimelineOffsetStorageKey(activeHouseholdId, sessionKey, currentUserId),
                JSON.stringify(offsets)
            );
        }
    };

    // Local fallback for offline / non-household mode
    useEffect(() => {
        if (sessionDocRef) return;
        const initialMap: Record<string, number> = {};
        recipes.forEach(mr => { initialMap[mr.recipe.id] = mr.plannedServings; });
        setServingsMap(prev => ({ ...initialMap, ...prev }));
    }, [recipes, sessionDocRef]);

    const futurePlannedInfo = useMemo(() => {
        if (!mealPlan || !currentDay || !orderedDays) return [];

        const currentIndex = orderedDays.indexOf(currentDay);
        if (currentIndex === -1) return [];
        const futureDays = orderedDays.slice(currentIndex + 1);

        const info: {
            recipeId: string;
            recipeName: string;
            days: string[];
            totalServings: number;
            totalMeals: number;
        }[] = [];

        liveRecipes.forEach(mr => {
            const daysWithRecipe: DayOfWeek[] = [];
            let futureServings = 0;
            let futureMealsCount = 0;
            futureDays.forEach(day => {
                const dayMeals = mealPlan[day] || [];
                dayMeals.forEach(m => {
                    if (m.recipeId === mr.recipe.id) {
                        daysWithRecipe.push(day);
                        futureServings += m.plannedServings;
                        futureMealsCount++;
                    }
                });
            });

            if (daysWithRecipe.length > 0) {
                const totalServings = mr.plannedServings + futureServings;
                const totalMeals = 1 + futureMealsCount;

                info.push({
                    recipeId: mr.recipe.id,
                    recipeName: mr.recipe.name,
                    days: daysWithRecipe.map(d => t('day_' + d)),
                    totalServings,
                    totalMeals,
                });
            }
        });
        return info;
    }, [liveRecipes, mealPlan, currentDay, orderedDays, t]);

    const consolidatedPrepList = useMemo(() => {
        const list: Record<string, { ingredient: Ingredient; sources: { recipeId: string; recipeName: string; quantity: number }[] }> = {};
        liveRecipes.forEach(mr => {
            const recipe = mr.recipe;
            const currentServings = servingsMap[recipe.id] ?? mr.plannedServings;
            const factor = currentServings / recipe.servings;
            recipe.ingredients.forEach(ing => {
                const key = `${ing.name.toLowerCase().trim()}_${ing.unit.toLowerCase().trim()}`;
                const scaledQty = ing.quantity * factor;
                if (list[key]) {
                    list[key].ingredient.quantity += scaledQty;
                    const existingSource = list[key].sources.find(s => s.recipeId === recipe.id);
                    if (existingSource) {
                        existingSource.quantity += scaledQty;
                    } else {
                        list[key].sources.push({ recipeId: recipe.id, recipeName: recipe.name, quantity: scaledQty });
                    }
                } else {
                    list[key] = {
                        ingredient: { ...ing, quantity: scaledQty },
                        sources: [{ recipeId: recipe.id, recipeName: recipe.name, quantity: scaledQty }]
                    };
                }
            });
        });
        return Object.values(list).sort((a, b) => {
            if (a.ingredient.isMeat && !b.ingredient.isMeat) return -1;
            if (!a.ingredient.isMeat && b.ingredient.isMeat) return 1;
            return a.ingredient.name.localeCompare(b.ingredient.name);
        });
    }, [liveRecipes, servingsMap]);

    const toggleStrike = async (key: string) => {
        const next = new Set(struckIngredients);
        if (next.has(key)) next.delete(key); else next.add(key);

        if (sessionDocRef) {
            await Firestore.updateDoc(sessionDocRef, { struckIngredients: Array.from(next) });
        } else {
            setStruckIngredients(next);
        }
    };

    const togglePrepStrike = async (ingredient: Ingredient, sourceRecipeIds: readonly string[]) => {
        const mealKey = `${ingredient.name}-${ingredient.unit}`;
        const recipeKeys = sourceRecipeIds.map((recipeId) =>
            getRecipePrepIngredientKey(recipeId, ingredient.name, ingredient.unit)
        );
        const next = new Set(preppedIngredients);
        const shouldMark = !next.has(mealKey);

        if (shouldMark) {
            next.add(mealKey);
            recipeKeys.forEach((key) => next.add(key));
        } else {
            next.delete(mealKey);
            recipeKeys.forEach((key) => next.delete(key));
        }

        if (sessionDocRef) {
            await Firestore.updateDoc(sessionDocRef, { preppedIngredients: Array.from(next) });
        } else {
            setPreppedIngredients(next);
        }
    };

    const handleUpdateServings = async (recipeId: string, newCount: number) => {
        const nextCount = Math.max(0.5, newCount);
        const nextServings = { ...servingsMap, [recipeId]: nextCount };

        if (sessionDocRef) {
            await Firestore.updateDoc(sessionDocRef, { servingsMap: nextServings });
        } else {
            setServingsMap(nextServings);
        }
    };

    const toggleStepComplete = async (key: string) => {
        const next = new Set(completedSteps);
        if (next.has(key)) next.delete(key); else next.add(key);
        setMealDonePromptDismissed(false);

        if (sessionDocRef) {
            await Firestore.updateDoc(sessionDocRef, { completedSteps: Array.from(next) });
        } else {
            setCompletedSteps(next);
        }
    };

    const markRecipeDone = async (recipeId: string) => {
        const mealRecipe = liveRecipes.find((mr) => mr.recipe.id === recipeId);
        if (!mealRecipe) return;

        const sections = groupRecipeInstructions(mealRecipe.recipe, t('prepTab'));
        const stepKeys = getRecipeStepKeys(recipeId, sections);
        const nextSteps = new Set(completedSteps);
        stepKeys.forEach((key) => nextSteps.add(key));
        const nextRecipes = new Set(completedRecipes);
        nextRecipes.add(recipeId);
        setMealDonePromptDismissed(false);

        if (sessionDocRef) {
            await Firestore.updateDoc(sessionDocRef, {
                completedSteps: Array.from(nextSteps),
                completedRecipes: Array.from(nextRecipes),
            });
        } else {
            setCompletedSteps(nextSteps);
            setCompletedRecipes(nextRecipes);
        }
    };

    const allMealStepsComplete = useMemo(
        () => areAllMealRecipesStepsComplete(liveRecipes, completedSteps, t('prepTab'), completedRecipes),
        [liveRecipes, completedSteps, completedRecipes, t]
    );

    useEffect(() => {
        if (allMealStepsComplete && !mealCompleted && !mealDonePromptDismissed) {
            setIsMealDonePromptOpen(true);
        }
    }, [allMealStepsComplete, mealCompleted, mealDonePromptDismissed]);

    const handleMarkMealDone = async () => {
        if (sessionDocRef) {
            await Firestore.updateDoc(sessionDocRef, {
                mealCompleted: true,
                cookingStartedAt: deleteField(),
                updatedAt: Firestore.serverTimestamp(),
            });
        } else {
            setMealCompleted(true);
            setCookingStartedAt(null);
        }

        setIsMealDonePromptOpen(false);
        setIsMealCompletedNoticeOpen(true);
        setView('menu');
    };

    const handleStartCooking = async (startTime: Date) => {
        const iso = startTime.toISOString();
        if (sessionDocRef) {
            await Firestore.updateDoc(sessionDocRef, {
                cookingStartedAt: iso,
                updatedAt: Firestore.serverTimestamp(),
            });
        } else {
            setCookingStartedAt(startTime);
        }
    };

    const handleResetStartTime = async (startTime: Date) => {
        await handleStartCooking(startTime);
    };

    const handleStopCookingSession = async () => {
        if (sessionDocRef) {
            await Firestore.updateDoc(sessionDocRef, {
                cookingStartedAt: deleteField(),
                updatedAt: Firestore.serverTimestamp(),
            });
        } else {
            setCookingStartedAt(null);
        }
    };

    const menuTitle = useMemo(() => (
        resolveMealMenuTitle(mealPlan, currentDay, mealType as MealType, t)
    ), [mealPlan, currentDay, mealType, t]);

    const handleMenuTitleChange = (nextTitle: string | null) => {
        if (!currentDay || !onUpdateMealSlotTitle) return;
        onUpdateMealSlotTitle(currentDay, mealType as MealType, nextTitle);
    };

    const activeMealRecipe = useMemo(() => {
        if (view === 'menu' || view === 'prep') return null;
        return liveRecipes.find(mr => mr.recipe.id === view) || null;
    }, [view, liveRecipes]);

    const activeFutureInfo = useMemo(() => {
        if (!activeMealRecipe) return null;
        return futurePlannedInfo.find(x => x.recipeId === activeMealRecipe.recipe.id) || null;
    }, [activeMealRecipe, futurePlannedInfo]);

    const isActiveRecipeDone = useMemo(() => {
        if (!activeMealRecipe) return false;
        const sections = groupRecipeInstructions(activeMealRecipe.recipe, t('prepTab'));
        return isRecipeStepsComplete(activeMealRecipe.recipe.id, sections, completedSteps)
            || completedRecipes.has(activeMealRecipe.recipe.id);
    }, [activeMealRecipe, completedSteps, completedRecipes, t]);

    const doneRecipeIds = useMemo(() => {
        const done = new Set(completedRecipes);
        liveRecipes.forEach((mealRecipe) => {
            const sections = groupRecipeInstructions(mealRecipe.recipe, t('prepTab'));
            if (isRecipeStepsComplete(mealRecipe.recipe.id, sections, completedSteps)) {
                done.add(mealRecipe.recipe.id);
            }
        });
        return done;
    }, [liveRecipes, completedRecipes, completedSteps, t]);

    const sessionModals = (
        <>
            <InfoNoticeModal
                isOpen={isMealDonePromptOpen}
                onClose={() => {
                    setIsMealDonePromptOpen(false);
                    setMealDonePromptDismissed(true);
                }}
                title={t('mealDonePromptTitle')}
                message={t('mealDonePromptMessage')}
                variant="success"
                language={language}
                actionLabel={t('markMealDone')}
                onAction={() => void handleMarkMealDone()}
            />
            <InfoNoticeModal
                isOpen={isMealCompletedNoticeOpen}
                onClose={() => setIsMealCompletedNoticeOpen(false)}
                title={t('mealCompletedTitle')}
                message={t('mealCompletedMessage')}
                variant="success"
                language={language}
                actionLabel={t('viewMenu')}
                onAction={() => setIsMealCompletedNoticeOpen(false)}
            />
        </>
    );

    const prepTimelineModal = (
        <PrepTimelineModal
            isOpen={isPrepTimelineOpen}
            onClose={() => setIsPrepTimelineOpen(false)}
            recipes={liveRecipes}
            language={language}
            encryptedGeminiKey={encryptedGeminiKey}
            mealLabel={menuTitle}
            cookingStartedAt={cookingStartedAt}
            onStartCooking={handleStartCooking}
            onResetStartTime={handleResetStartTime}
            onStopSession={handleStopCookingSession}
            savedChartRecipeIds={chartSelectedRecipeIds}
            onChartRecipeSelectionChange={handleChartRecipeSelectionChange}
            savedChartRecipeOffsets={chartRecipeOffsets}
            onChartRecipeOffsetsChange={handleChartRecipeOffsetsChange}
            completedSteps={completedSteps}
            completedRecipes={completedRecipes}
            onToggleStepComplete={toggleStepComplete}
        />
    );

    if (view === 'prep') {
        return (
            <>
                <PrepGatherScreen
                    consolidatedPrepList={consolidatedPrepList}
                    futurePlannedInfo={futurePlannedInfo}
                    servingsMap={servingsMap}
                    onUpdateServings={handleUpdateServings}
                    preppedIngredients={preppedIngredients}
                    onTogglePrepped={togglePrepStrike}
                    onAddIngredientsToCart={onAddIngredientsToCart}
                    language={language}
                    isHidden={isHidden}
                    mealLabel={t('cookingMeal').replace('{meal}', t(mealType.toLowerCase())) + (sessionDateDisplay ? ` · ${sessionDateDisplay}` : '')}
                    sessionDateString={sessionDateString}
                    onBack={() => setView('menu')}
                />
                {prepTimelineModal}
                {sessionModals}
            </>
        );
    }

    if (activeMealRecipe) {
        return (
            <>
                <ActiveCookingSessionScreen
                    mealRecipe={activeMealRecipe}
                    language={language}
                    isHidden={isHidden}
                    servings={servingsMap[activeMealRecipe.recipe.id] ?? activeMealRecipe.plannedServings}
                    onUpdateServings={(v) => handleUpdateServings(activeMealRecipe.recipe.id, v)}
                    struckIngredients={struckIngredients}
                    onToggleStruck={toggleStrike}
                    completedSteps={completedSteps}
                    onToggleStepComplete={toggleStepComplete}
                    isRecipeDone={isActiveRecipeDone}
                    onMarkRecipeDone={() => void markRecipeDone(activeMealRecipe.recipe.id)}
                    onAddIngredientsToCart={onAddIngredientsToCart}
                    onViewRecipe={onViewRecipe}
                    onBack={() => setView('menu')}
                    futureInfo={activeFutureInfo}
                    sessionDateString={sessionDateString}
                    showMealServingsContext
                />
                {prepTimelineModal}
                {sessionModals}
            </>
        );
    }

    return (
        <>
            <MenuOverviewScreen
                recipes={liveRecipes}
                title={menuTitle}
                language={language}
                isHidden={isHidden}
                onClose={onClose}
                onGatherIngredients={() => setView('prep')}
                onOpenPrepTimeline={() => setIsPrepTimelineOpen(true)}
                onStartPrep={(recipeId) => setView(recipeId)}
                completedRecipes={doneRecipeIds}
                mealCompleted={mealCompleted}
                canEditTitle={canManage}
                onTitleChange={handleMenuTitleChange}
            />
            {prepTimelineModal}
            {sessionModals}
        </>
    );
};

export default MealCookingSession;
